/**
 * Templates for the open workspace (flag `templates`): the gallery sheet ("+ New → Templates…", the sidebar "+",
 * the Projects hub, ⌘K "New from template") with categories, search, a preview and "Use template" (to the
 * Workspace, Private, or inside the page that was open); "Save as template" and "Duplicate" (page menu); renaming,
 * deleting and editing saved templates; who may save workspace templates. Online only (the gallery list is fetched
 * when it opens; creating needs the server, which seeds the new pages' bodies).
 */
import type {
  Locale,
  PageNode,
  PageTemplateInfo,
  PageVisibility,
  TemplateCategory,
  TemplateSettings,
} from "@bundle/contracts";
import { CUSTOM_TEMPLATE_PREFIX, DEFAULT_TEMPLATE_SETTINGS } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { templateSections, type TemplateFilter, type TemplateSection } from "../logic/templates.ts";
import { errorKind, type TemplatesApi } from "../ports.ts";
import type { PageTreeViewModel } from "./PageTreeViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Where "Use template" puts the new page. */
export type TemplateDestination = "workspace" | "private" | "inside";

export type SaveTemplateDraft = {
  pageId: string;
  /** The page's own title (the template keeps it as the content's title). */
  pageTitle: string;
  name: string;
  description: string;
  icon?: string;
  category: TemplateCategory;
  scope: "workspace" | "personal";
  includeRows: boolean;
  /** The page is, or contains, a database (only then does "Include database rows" mean anything). */
  hasDatabases: boolean;
  saving: boolean;
  error?: MessageDescriptor;
};

export type TemplatesState = {
  /** The gallery sheet is open. */
  open: boolean;
  status: "idle" | "loading" | "ready" | "error";
  templates: PageTemplateInfo[];
  sections: TemplateSection[];
  filter: TemplateFilter;
  query: string;
  selected?: PageTemplateInfo;
  destination: TemplateDestination;
  /** The page open when the gallery opened ("inside" target), if I can add sub-pages to it. */
  context?: { pageId: string; title: string };
  startEmpty: boolean;
  creating: boolean;
  error?: MessageDescriptor;
  canCreateWorkspace: boolean;
  canCreatePersonal: boolean;
  canManageSettings: boolean;
  settings: TemplateSettings;
  /** I may create top-level pages (Workspace / Private destinations). */
  canCreatePages: boolean;
  /** "Save as template" sheet. */
  save?: SaveTemplateDraft;
  /** A short confirmation (saved, deleted). */
  notice?: MessageDescriptor;
  busy: boolean;
};

export type OpenGallery = {
  /** The page being viewed (offers "inside this page"). */
  pageId?: string;
  /** Preferred destination section. */
  visibility?: PageVisibility;
  filter?: TemplateFilter;
};

export class TemplatesViewModel extends ViewModel<TemplatesState> {
  readonly ws: WorkspaceViewModel;
  private readonly tree: PageTreeViewModel;
  /** The template routes (row templates and their defaults use them too). */
  readonly api: TemplatesApi;
  private locale: Locale = "en";
  private loadSeq = 0;

  constructor(ws: WorkspaceViewModel, tree: PageTreeViewModel, api: TemplatesApi) {
    super({
      open: false,
      status: "idle",
      templates: [],
      sections: [],
      filter: "all",
      query: "",
      destination: "workspace",
      startEmpty: false,
      creating: false,
      canCreateWorkspace: false,
      canCreatePersonal: false,
      canManageSettings: false,
      settings: DEFAULT_TEMPLATE_SETTINGS,
      canCreatePages: tree.state.canCreatePages,
      busy: false,
    });
    this.ws = ws;
    this.tree = tree;
    this.api = api;
    this.onDispose(tree.subscribe(() => this.update({ canCreatePages: tree.state.canCreatePages })));
  }

  /** The UI language: built-in names and the content of new pages follow it. */
  setLocale(locale: Locale): void {
    if (locale === this.locale) return;
    this.locale = locale;
    if (this.state.status !== "idle") void this.refresh();
  }

  // ---------- Gallery ----------

  openGallery(opts: OpenGallery = {}): void {
    const page = opts.pageId === undefined ? undefined : this.tree.page(opts.pageId);
    const insideOk = page !== undefined && page.kind === "page" && (page.myAccess === "full" || page.myAccess === "edit") && page.templateId === undefined;
    const canTop = this.tree.state.canCreatePages;
    const next: TemplatesState = {
      ...this.state,
      open: true,
      filter: opts.filter ?? "all",
      query: "",
      startEmpty: false,
      destination: !canTop && insideOk ? "inside" : opts.visibility === "private" ? "private" : "workspace",
      canCreatePages: canTop,
    };
    if (insideOk) next.context = { pageId: page.id, title: page.title };
    else delete next.context;
    delete next.error;
    this.setState(next);
    this.recompute();
    void this.refresh();
  }

  close(): void {
    if (!this.state.open) return;
    this.update({ open: false });
  }

  async refresh(): Promise<void> {
    const seq = ++this.loadSeq;
    if (this.state.templates.length === 0) this.update({ status: "loading" });
    try {
      const res = await this.api.list(this.ws.wsId, this.locale);
      if (seq !== this.loadSeq) return;
      const s = { ...this.state, status: "ready" as const, templates: res.templates, canCreateWorkspace: res.canCreateWorkspace, canCreatePersonal: res.canCreatePersonal, canManageSettings: res.canManageSettings, settings: res.settings };
      delete s.error;
      this.setState(s);
      this.recompute();
    } catch {
      if (seq !== this.loadSeq) return;
      this.update({ status: this.state.templates.length > 0 ? "ready" : "error", error: msg("templates.loadFailed") });
    }
  }

  private recompute(): void {
    const s = this.state;
    const sections = templateSections(s.templates, s.query, s.filter);
    const visible = sections.flatMap((x) => x.templates);
    const keep = s.selected === undefined ? undefined : visible.find((t) => t.ref === s.selected!.ref);
    const next: TemplatesState = { ...s, sections };
    const selected = keep ?? visible[0];
    if (selected === undefined) delete next.selected;
    else next.selected = selected;
    this.setState(next);
  }

  setFilter(filter: TemplateFilter): void {
    if (filter === this.state.filter) return;
    this.update({ filter });
    this.recompute();
  }

  setQuery(query: string): void {
    if (query === this.state.query) return;
    this.update({ query });
    this.recompute();
  }

  select(ref: string): void {
    const t = this.state.templates.find((x) => x.ref === ref);
    if (t !== undefined) this.update({ selected: t });
  }

  /** Moves the selection through the visible list (keyboard ↑/↓). */
  moveSelection(delta: number): void {
    const visible = this.state.sections.flatMap((x) => x.templates);
    if (visible.length === 0) return;
    const i = this.state.selected === undefined ? -1 : visible.findIndex((t) => t.ref === this.state.selected!.ref);
    const next = visible[Math.max(0, Math.min(visible.length - 1, i + delta))];
    if (next !== undefined) this.update({ selected: next });
  }

  setDestination(destination: TemplateDestination): void {
    if (destination === "inside" && this.state.context === undefined) return;
    if (destination !== "inside" && !this.state.canCreatePages) return;
    this.update({ destination });
  }

  setStartEmpty(startEmpty: boolean): void {
    this.update({ startEmpty });
  }

  /** Creates from the selected template; resolves with the page to open (undefined on failure: `error` is set). */
  async use(): Promise<PageNode | undefined> {
    const t = this.state.selected;
    if (t === undefined || this.state.creating) return undefined;
    const s = { ...this.state, creating: true };
    delete s.error;
    this.setState(s);
    const inside = this.state.destination === "inside" ? this.state.context : undefined;
    const page = await this.tree.create({
      template: t.ref,
      kind: t.kind,
      locale: this.locale,
      ...(this.state.startEmpty ? { startEmpty: true } : {}),
      ...(inside !== undefined ? { parentId: inside.pageId } : { visibility: this.state.destination === "private" ? "private" : "workspace" }),
    });
    this.tree.dismissError();
    if (page === undefined) {
      this.update({ creating: false, error: msg("templates.useFailed") });
      return undefined;
    }
    this.update({ creating: false, open: false });
    return page;
  }

  async rename(templateId: string, name: string): Promise<void> {
    const trimmed = name.trim();
    if (trimmed === "") return;
    await this.mutate(async () => {
      const t = await this.api.updateTemplate(this.ws.wsId, templateId, { name: trimmed });
      this.replace(t);
    });
  }

  async remove(templateId: string): Promise<void> {
    await this.mutate(async () => {
      await this.api.deleteTemplate(this.ws.wsId, templateId);
      this.update({ templates: this.state.templates.filter((t) => t.id !== templateId) });
      this.recompute();
    });
  }

  async setWorkspaceCreators(value: TemplateSettings["workspaceTemplateCreators"]): Promise<void> {
    await this.mutate(async () => {
      const settings = await this.api.updateSettings(this.ws.wsId, { workspaceTemplateCreators: value });
      this.update({ settings });
      await this.refresh();
    });
  }

  private replace(t: PageTemplateInfo): void {
    this.update({ templates: this.state.templates.map((x) => (x.ref === t.ref ? t : x)) });
    this.recompute();
  }

  private async mutate(fn: () => Promise<void>): Promise<void> {
    this.update({ busy: true });
    try {
      await fn();
      const s = { ...this.state, busy: false };
      delete s.error;
      this.setState(s);
    } catch (e) {
      this.update({ busy: false, error: errorKind(e) === "forbidden" ? msg("templates.editing.readOnly") : msg("templates.loadFailed") });
    }
  }

  // ---------- Save as template ----------

  /** Opens the "Save as template" sheet for a page or database. */
  openSave(pageId: string): void {
    const page = this.tree.page(pageId);
    const title = page?.title ?? "";
    void this.refresh();
    this.update({
      save: {
        pageId,
        pageTitle: title,
        name: title,
        description: "",
        ...(page?.icon !== undefined ? { icon: page.icon } : {}),
        category: page?.kind === "database" ? "projects" : "knowledge",
        scope: "personal",
        includeRows: false,
        hasDatabases: this.containsDatabase(pageId),
        saving: false,
      },
    });
  }

  /**
   * The page is a database, or one sits somewhere below it. Unknown (no tree yet, or children the tree hasn't loaded)
   * counts as yes, so the option is only hidden when there is certainly nothing to copy.
   */
  private containsDatabase(pageId: string): boolean {
    const pages = this.tree.state.cache?.pages;
    if (pages === undefined || pages[pageId] === undefined) return true;
    const children = new Map<string, string[]>();
    for (const p of Object.values(pages)) {
      if (p.parentId === undefined || p.kind === "row") continue;
      children.set(p.parentId, [...(children.get(p.parentId) ?? []), p.id]);
    }
    const seen = new Set<string>();
    const stack = [pageId];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const page = pages[id];
      if (page?.kind === "database") return true;
      const kids = children.get(id) ?? [];
      if (kids.length === 0 && page?.hasChildren === true) return true;
      stack.push(...kids);
    }
    return false;
  }

  setSaveDraft(patch: Partial<Pick<SaveTemplateDraft, "name" | "description" | "icon" | "category" | "scope" | "includeRows">>): void {
    const d = this.state.save;
    if (d === undefined) return;
    if (patch.scope === "workspace" && !this.state.canCreateWorkspace) return;
    const next: SaveTemplateDraft = { ...d, ...patch };
    delete next.error;
    this.update({ save: next });
  }

  cancelSave(): void {
    if (this.state.save === undefined) return;
    const s = { ...this.state };
    delete s.save;
    this.setState(s);
  }

  /** Saves the template; resolves with it (the sheet closes and a notice shows). */
  async submitSave(): Promise<PageTemplateInfo | undefined> {
    const d = this.state.save;
    if (d === undefined || d.saving || d.name.trim() === "") return undefined;
    this.update({ save: { ...d, saving: true } });
    try {
      const req = {
        name: d.name.trim(),
        category: d.category,
        scope: d.scope,
        includeRows: d.includeRows,
        clientId: this.ws.deps.ids.next(),
        ...(d.description.trim() !== "" ? { description: d.description.trim() } : {}),
        ...(d.icon !== undefined ? { icon: d.icon } : {}),
      };
      const t = await this.api.saveAsTemplate(this.ws.wsId, d.pageId, req);
      const s = { ...this.state, templates: [...this.state.templates.filter((x) => x.ref !== t.ref), t], notice: msg("templates.save.saved", { name: t.name }) };
      delete s.save;
      this.setState(s);
      this.recompute();
      return t;
    } catch (e) {
      const cur = this.state.save;
      if (cur !== undefined) {
        const error = errorKind(e) === "forbidden" && d.scope === "workspace" ? msg("templates.save.workspaceAdminsOnly") : msg("templates.save.failed");
        this.update({ save: { ...cur, saving: false, error } });
      }
      return undefined;
    }
  }

  /** "Save row as template": a row template of the row's database from its cells and body; resolves with its id (its page). */
  async saveRowAsTemplate(databaseId: string, rowId: string, name: string): Promise<string | undefined> {
    try {
      const t = await this.api.createRowTemplate(this.ws.wsId, databaseId, { name: name.trim(), fromRowId: this.ws.deps.data.resolveId(this.ws.wsId, rowId), clientId: this.ws.deps.ids.next() });
      return t.id;
    } catch {
      this.update({ notice: msg("templates.rowTemplates.failed") });
      return undefined;
    }
  }

  // ---------- Duplicate ----------

  /** Duplicates a page, database or row next to it; resolves with the copy to open (undefined: `notice` explains). */
  async duplicate(pageId: string): Promise<PageNode | undefined> {
    try {
      const res = await this.api.duplicatePage(this.ws.wsId, pageId, { locale: this.locale, clientId: this.ws.deps.ids.next() });
      for (const p of res.pages) this.tree.remember(p);
      return res.page;
    } catch {
      this.update({ notice: msg("templates.duplicateFailed") });
      return undefined;
    }
  }

  dismissNotice(): void {
    if (this.state.notice === undefined) return;
    const s = { ...this.state };
    delete s.notice;
    this.setState(s);
  }

  dismissError(): void {
    if (this.state.error === undefined) return;
    const s = { ...this.state };
    delete s.error;
    this.setState(s);
  }
}

/** The template reference of a saved template id. */
export function customTemplateRef(templateId: string): string {
  return `${CUSTOM_TEMPLATE_PREFIX}${templateId}`;
}
